import { Component, Input, OnChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

interface Sale { label: string; price: number; t: number; }
interface ResoldRow { plate: string; buy: number; buyT: number; sales: Sale[]; }
interface Pt { year: number; value: number; lo: number; hi: number; }

interface ResoldFile { generated: string; series: { [year: string]: { plate: string; buy: number; buyDate: string; sales: { date: string; price: number }[] }[] }; }
interface IndexFile { series: { current: { points: { year: number; value: number; ci_lo: number; ci_hi: number }[] } }; }

// Both assets are static and shared by the hero strip and the section, so fetch once per session.
let assets$: Observable<[ResoldFile | null, IndexFile | null]> | null = null;

const WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five'];

// Heading themes, rotated by series so neighbouring years read differently.
// n is how many plates the series shows (1 to 5), so the copy reads right for a single standout.
const THEMES: { eyebrow: string; title: (y: string, n: number) => string; intro: (y: string, n: number) => string }[] = [
  { eyebrow: 'Best performers',
    title: (y, n) => n > 1 ? `The ${y}s that paid off.` : `The ${y} that paid off.`,
    intro: (y, n) => `Strong words and names that DVLA sold at the fixed price rather than holding back for auction. Here's what ${n > 1 ? `the best ${y}s` : 'the best one'} went on to fetch.` },
  { eyebrow: 'Hidden at the fixed price',
    title: (y, n) => n > 1 ? `${WORDS[n] || n} ${y}s that slipped through.` : `The ${y} that slipped through.`,
    intro: () => `Every release, a few gems get past DVLA's auction list and into the fixed-price sale. Spot them early and this is what can happen.` },
  { eyebrow: 'Top fixed-price finds',
    title: (y, n) => n > 1 ? `The best buys of the ${y} series.` : `The best buy of the ${y} series.`,
    intro: (y, n) => `Bought from DVLA at the fixed price, sold later at auction. ${n > 1 ? `The ${y}s with the biggest gains` : `The ${y} with the biggest gain`} so far.` },
  { eyebrow: 'Investment grade',
    title: (y, n) => n > 1 ? `The standout ${y}s, bought from DVLA.` : `The standout ${y}, bought from DVLA.`,
    intro: (y, n) => `${n > 1 ? 'Clear reads that buyers fought over' : 'A clear read that buyers fought over'} once it came to auction. ${n > 1 ? 'Each one' : 'It'} started life at the fixed price.` },
  { eyebrow: 'Treasure found',
    title: (y, n) => n > 1 ? `What the sharpest ${y} buyers made.` : `What the sharpest ${y} buyer made.`,
    intro: (y, n) => `The treasure hunt starts at release. ${n > 1 ? 'These buyers' : 'This buyer'} found theirs at the fixed price and sold at auction for a great deal more.` },
];

// Published nightly by analysis/best_performers in platex-valuation-lab (read-only from the
// sold data), so new auction resales appear without a site release.
const DATA_URL = 'https://cdn.platex.co.uk/data/new-release/best-performers.json';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ORDER = ['51', '02', '52', '03', '53', '04', '54', '05', '55', '06', '56', '07', '57', '08', '58', '09', '59',
  '10', '60', '11', '61', '12', '62', '13', '63', '14', '64', '15', '65', '16', '66', '17', '67', '18', '68', '19',
  '69', '20', '70', '21', '71', '22', '72', '23', '73', '24', '74', '25', '75', '26', '76', '27'];

@Component({
  selector: 'pla-new-release-resold',
  templateUrl: './new-release-resold.component.html',
  styleUrls: ['./new-release-resold.component.scss'],
})
export class NewReleaseResoldComponent implements OnChanges {
  @Input() year = '';
  @Input() mode: 'strip' | 'section' = 'section';

  rows: ResoldRow[] = [];
  private index: Pt[] = [];
  private file: ResoldFile | null = null;
  theme = THEMES[0];
  sel = 0;
  readonly W = 560;
  readonly H = 330;
  readonly P = { l: 54, r: 18, t: 22, b: 30 };
  x0 = 2009; x1 = 2027; lo = 100; hi = 30000;
  plateLine = ''; indexLine = ''; indexDash = ''; band = '';
  dots: { x: number; y: number; label: string; anchor: string }[] = [];
  yTicks: { y: number; label: string }[] = [];
  xTicks: { x: number; label: string }[] = [];
  buyX = 0; buyY = 0;
  indexEnd = 0;
  /** False when the purchase predates the index (it starts in 2010), so there is nothing to compare against. */
  hasIndex = true;

  constructor(private http: HttpClient) {}

  ngOnChanges(): void {
    if (!assets$) {
      assets$ = forkJoin([
        this.http.get<ResoldFile>(DATA_URL).pipe(catchError(() => of(null))),
        this.http.get<IndexFile>('assets/market-index/platex-index-series.json').pipe(catchError(() => of(null))),
      ]).pipe(shareReplay(1));
    }
    assets$.subscribe(([file, idx]) => {
      this.file = file;
      this.index = (idx?.series?.current?.points || [])
        .map((p) => ({ year: p.year, value: p.value, lo: p.ci_lo ?? p.value, hi: p.ci_hi ?? p.value }));
      this.load();
    });
  }

  private load(): void {
    const toT = (d: string) => Number(d.slice(0, 4)) + (Number(d.slice(5, 7)) - 1) / 12;
    const toLabel = (d: string) => MONTHS[Number(d.slice(5, 7)) - 1] + ' ' + d.slice(0, 4);
    this.rows = (this.index.length ? this.file?.series?.[this.year] || [] : []).map((r) => ({
      plate: r.plate, buy: r.buy, buyT: toT(r.buyDate),
      sales: r.sales.map((x) => ({ label: toLabel(x.date), price: x.price, t: toT(x.date) })),
    }));
    const i = Math.max(0, ORDER.indexOf(this.year));
    this.theme = THEMES[i % THEMES.length];
    this.sel = 0;
    if (this.rows.length) this.plot();
  }

  select(i: number): void { this.sel = i; this.plot(); }

  // A bare #resold href resolves against <base href="/"> and lands on the home page, so scroll instead.
  toSection(e: Event): void {
    e.preventDefault();
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    document.getElementById('resold')?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  first(r: ResoldRow): Sale { return r.sales[0]; }
  roi(r: ResoldRow): string {
    return '+' + Math.round((this.first(r).price - r.buy) / r.buy * 100).toLocaleString('en-GB') + '%';
  }
  years(r: ResoldRow): number { return this.first(r).t - r.buyT; }
  annual(r: ResoldRow): string | null {
    const y = this.years(r);
    if (y < 1) return null;
    return (Math.pow(this.first(r).price / r.buy, 1 / y) * 100 - 100).toFixed(1) + '% a year';
  }
  held(r: ResoldRow): string {
    const m = Math.round(this.years(r) * 12);
    if (m < 12) return m === 1 ? '1 month' : m + ' months';
    const y = Math.round(m / 12);
    return y === 1 ? '1 year' : y + ' years';
  }
  gain(r: ResoldRow): string { return this.gbp(this.first(r).price - r.buy); }
  gbp(v: number): string { return '£' + Math.round(v).toLocaleString('en-GB'); }
  onSale(r: ResoldRow): string {
    return MONTHS[Math.round((r.buyT % 1) * 12)] + ' ' + Math.floor(r.buyT);
  }

  private at(t: number, k: 'value' | 'lo' | 'hi'): number {
    const y = Math.floor(t), f = t - y;
    const a = this.index.find((p) => p.year === y) || this.index[this.index.length - 1];
    const b = this.index.find((p) => p.year === y + 1) || a;
    return a[k] + (b[k] - a[k]) * f;
  }
  private sx(t: number): number {
    return this.P.l + (t - this.x0) / (this.x1 - this.x0) * (this.W - this.P.l - this.P.r);
  }
  // Log scale: a plate that went 40x and an index that moved 30% both stay readable.
  private sy(v: number): number {
    const f = (Math.log10(v) - Math.log10(this.lo)) / (Math.log10(this.hi) - Math.log10(this.lo));
    return this.H - this.P.b - f * (this.H - this.P.t - this.P.b);
  }

  private plot(): void {
    const r = this.rows[this.sel];
    const last = r.sales[r.sales.length - 1].t;
    const firstYear = this.index[0].year, lastYear = this.index[this.index.length - 1].year;
    this.hasIndex = r.buyT >= firstYear;
    this.x0 = this.hasIndex ? Math.max(firstYear, Math.floor(r.buyT) - 2) : Math.floor(r.buyT) - 1;
    this.x1 = Math.min(lastYear + 0.83, Math.ceil(last + 0.5));
    const base = this.at(r.buyT, 'value');
    const scale = (v: number) => r.buy * v / base;

    const vals = [r.buy, ...r.sales.map((s) => s.price)];
    const idxPts: { t: number; v: number; lo: number; hi: number }[] = [];
    for (let t = this.x0; t <= this.x1 + 0.001; t += 0.25) {
      idxPts.push({ t, v: scale(this.at(t, 'value')), lo: scale(this.at(t, 'lo')), hi: scale(this.at(t, 'hi')) });
    }
    if (this.hasIndex) idxPts.forEach((p) => vals.push(p.lo, p.hi));
    const nice = [100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
    this.lo = [...nice].reverse().find((n) => n <= Math.min(...vals) * 0.9) || 100;
    this.hi = nice.find((n) => n >= Math.max(...vals) * 1.15) || 100000;
    const ticks = Math.log10(this.hi / this.lo) > 2 ? [100, 1000, 10000, 100000, 500, 5000, 50000] : nice;
    this.yTicks = ticks.filter((n) => n >= this.lo && n <= this.hi)
      .map((n) => ({ y: this.sy(n), label: n >= 1000 ? '£' + n / 1000 + 'k' : '£' + n }));
    const span = this.x1 - this.x0;
    const step = span > 12 ? 5 : span > 5 ? 2 : 1;
    this.xTicks = [];
    for (let y = Math.ceil(this.x0 / step) * step; y <= this.x1; y += step) {
      this.xTicks.push({ x: this.sx(y), label: String(y) });
    }

    const path = (a: { t: number; v: number }[]) =>
      a.map((p, i) => (i ? 'L' : 'M') + this.sx(p.t).toFixed(1) + ' ' + this.sy(p.v).toFixed(1)).join(' ');
    // The latest index year is year to date, so it is dashed as on the index chart.
    const solidTo = lastYear - 1;
    this.indexLine = path(idxPts.filter((p) => p.t <= solidTo + 0.001));
    this.indexDash = path(idxPts.filter((p) => p.t >= solidTo - 0.001));
    this.band = path(idxPts.map((p) => ({ t: p.t, v: p.hi }))) + ' ' +
      path([...idxPts].reverse().map((p) => ({ t: p.t, v: p.lo }))).replace(/^M/, 'L') + ' Z';

    const pts = [{ t: r.buyT, v: r.buy }, ...r.sales.map((s) => ({ t: s.t, v: s.price }))];
    this.plateLine = path(pts);
    this.buyX = this.sx(r.buyT);
    this.buyY = this.sy(r.buy);
    this.dots = r.sales.map((s, i) => ({
      x: this.sx(s.t), y: this.sy(s.price), label: i === 0 ? this.gbp(s.price) : '',
      anchor: 'end',
    }));
    this.indexEnd = scale(this.at(this.first(r).t, 'value'));
  }
}
